import React, { useState, useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { useSalesService } from 'services/sales';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES, ROUTES } from 'utils/constants';
import { Sales as SalesType } from 'types/sales';
import Category from 'types/category';
import Toast from 'components/Toast';
import styles from './ViewSales.module.scss';

function ViewSales() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const salesService = useSalesService();
  const apiClient = useApiClient();
  const [sale, setSale] = useState<SalesType | null>(null);
  const [categories, setCategories] = useState<{ [key: string]: Category }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{
    message: string;
    type: 'success' | 'error';
  } | null>(null);

  const showToast = useCallback(
    (message: string, type: 'success' | 'error') => {
      setToast({ message, type });
    },
    [],
  );

  // Fetch categories
  const loadCategories = useCallback(async () => {
    try {
      const categoriesData = await apiClient.get<{
        [key: string]: Category;
      }>(API_ROUTES.CATEGORY);
      if (categoriesData) {
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Failed to load categories:', error);
    }
  }, [apiClient]);

  // Load sale
  const loadSale = useCallback(async () => {
    if (!id) return;

    setLoading(true);
    setError(null);
    try {
      const data = await salesService.getSalesById(id);
      setSale(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error
          ? err.message
          : 'Failed to load sale. Please try again.';
      setError(errorMessage);
      showToast(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  }, [id, salesService, showToast]);

  useEffect(() => {
    void loadCategories();
  }, [loadCategories]);

  useEffect(() => {
    void loadSale();
  }, [loadSale]);

  // Format currency
  const formatCurrency = (amount: number): string => {
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date
  const formatDate = (dateString: string): string => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(date);
  };

  // Get category name
  const getCategoryName = (categoryId: string): string => {
    return categories[categoryId]?.name || 'Unknown Category';
  };

  // Get product entries for items/services sold
  const productEntries = sale?.productEntries || [];

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingState}>Loading sale details...</div>
      </div>
    );
  }

  if (error || !sale) {
    return (
      <div className={styles.container}>
        <div className={styles.errorState}>
          <p>{error || 'Sale not found'}</p>
          <button
            className={styles.backButton}
            onClick={() => navigate(ROUTES.SALES.ROOT)}
          >
            <ArrowLeft size={20} />
            Back to Sales
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.pageHeader}>
        <button
          className={styles.backButton}
          onClick={() => navigate(ROUTES.SALES.ROOT)}
          aria-label="Go back"
        >
          <ArrowLeft size={20} />
        </button>
        {sale.isVoided && <span className={styles.voidedBadge}>VOIDED</span>}
      </div>

      <div className={styles.invoiceContainer}>
        {/* Invoice Header */}
        <div className={styles.invoiceHeader}>
          <div className={styles.companyInfo}>
            <h1 className={styles.companyName}>
              {sale.company?.name || 'Company Name'}
            </h1>
            {sale.company?.description && (
              <p className={styles.companyDescription}>
                {sale.company.description}
              </p>
            )}
          </div>
          <div className={styles.invoiceInfo}>
            <h2 className={styles.invoiceTitle}>SALES INVOICE</h2>
            <div className={styles.invoiceMeta}>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Invoice #:</span>
                <span className={styles.metaValue}>{sale.id.slice(0, 8)}</span>
              </div>
              <div className={styles.metaRow}>
                <span className={styles.metaLabel}>Date:</span>
                <span className={styles.metaValue}>
                  {formatDate(sale.date)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className={styles.paymentSummary}>
          <p className={styles.paymentSummaryText}>
            ${formatCurrency(sale.totalCashOrAr)} paid on{' '}
            {formatDate(sale.date)}
          </p>
        </div>

        {/* Customer Information */}
        {sale.customer && (
          <div className={styles.customerSection}>
            <h3 className={styles.sectionTitle}>Customer</h3>
            <div className={styles.customerInfo}>
              <p className={styles.customerName}>{sale.customer.name}</p>
              {sale.customer.email && (
                <p className={styles.customerDetail}>
                  Email: {sale.customer.email}
                </p>
              )}
              {sale.customer.phone && (
                <p className={styles.customerDetail}>
                  Phone: {sale.customer.phone}
                </p>
              )}
            </div>
          </div>
        )}

        {/* Items/Services Sold Table */}
        <div className={styles.itemsSection}>
          <h3 className={styles.sectionTitle}>Items / Services Sold</h3>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th className={styles.itemHeader}>#</th>
                <th className={styles.itemHeader}>Description</th>
                <th className={styles.itemHeaderAmount}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {productEntries.length > 0 ? (
                productEntries.map((entry, index) => {
                  const productName = entry.productName || 'Unknown Product';
                  const productPrice = entry.productPrice || 0;
                  const quantity = entry.quantity || 0;
                  const totalAmount = quantity * productPrice;
                  return (
                    <tr
                      key={`product-${entry.productId}-${index}`}
                      className={styles.itemRow}
                    >
                      <td className={styles.itemNumber}>{index + 1}</td>
                      <td className={styles.itemDescription}>{productName}</td>
                      <td className={styles.itemAmount}>
                        ${formatCurrency(totalAmount)}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan={3} className={styles.emptyRow}>
                    No items found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Payment Methods Table */}
        <div className={styles.paymentSection}>
          <h3 className={styles.sectionTitle}>Payment Methods</h3>
          <table className={styles.itemsTable}>
            <thead>
              <tr>
                <th className={styles.itemHeader}>#</th>
                <th className={styles.itemHeader}>Payment Method</th>
                <th className={styles.itemHeaderAmount}>Amount</th>
              </tr>
            </thead>
            <tbody>
              {(sale.cashOrArEntries || []).map((entry, index) => (
                <tr key={`cash-ar-${index}`} className={styles.itemRow}>
                  <td className={styles.itemNumber}>{index + 1}</td>
                  <td className={styles.itemDescription}>
                    {getCategoryName(entry.categoryId)}
                  </td>
                  <td className={styles.itemAmount}>
                    ${formatCurrency(entry.amount)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Notes/Description Section */}
        {sale.description && (
          <div className={styles.notesSection}>
            <label className={styles.notesLabel}>Notes</label>
            <div className={styles.notesTextarea}>{sale.description}</div>
          </div>
        )}

        {/* Totals Section */}
        <div className={styles.totalsSection}>
          <div className={styles.totalsTable}>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Subtotal:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(sale.totalRevenue)}
              </span>
            </div>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Total:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(sale.totalRevenue)}
              </span>
            </div>
            <div className={styles.totalRow}>
              <span className={styles.totalLabel}>Amount Paid:</span>
              <span className={styles.totalValue}>
                ${formatCurrency(sale.totalCashOrAr)}
              </span>
            </div>
          </div>
        </div>

        {/* Void Information */}
        {sale.isVoided && sale.voidReason && (
          <div className={styles.voidInfo}>
            <h3 className={styles.voidTitle}>Void Information</h3>
            <div className={styles.voidInfoRow}>
              <strong>Voided At:</strong>
              <span>{sale.voidedAt ? formatDate(sale.voidedAt) : 'N/A'}</span>
            </div>
            <div className={styles.voidInfoRow}>
              <strong>Reason:</strong>
              <span>{sale.voidReason}</span>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className={styles.invoiceFooter}>
          <p className={styles.footerText}>
            This is a system-generated sales invoice. For questions, please
            contact support.
          </p>
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => {
            setToast(null);
          }}
        />
      )}
    </div>
  );
}

export default ViewSales;
